import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, User, Users, Calendar, Grid, BookOpen, Star } from 'lucide-react';
import { ProjectWithComponents, Component } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import { useData } from '../contexts/DataContext';
import { formatDate } from '../utils/filters';

interface PersonStats {
  name: string;
  role: 'Owner' | 'Maintainer';
  projects: string[];
  totalProjects: number;
  totalComponents: number;
  lastActivity: string;
}

const PersonDetail: React.FC = () => {
  const { personName } = useParams<{ personName: string }>();
  const [ownedProjects, setOwnedProjects] = useState<ProjectWithComponents[]>([]);
  const [maintainedProjects, setMaintainedProjects] = useState<ProjectWithComponents[]>([]);
  const [components, setComponents] = useState<Component[]>([]);
  const [personStats, setPersonStats] = useState<PersonStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Use DataContext instead of old hooks
  const { data, isLoading, error: contextError } = useData();
  const projects = data.projects;
  const allComponents = useMemo(() => data.projects.flatMap(p => p.components), [data.projects]);

  // Update local state when API data changes
  useEffect(() => {
    if (allComponents) {
      setComponents(allComponents);
    }
  }, [allComponents]);

  // Extract person stats when projects data changes
  useEffect(() => {
    if (projects && personName) {
      const decodedName = decodeURIComponent(personName);
      
      // Find projects where this person is an owner or maintainer
      const ownedProjectsList = projects.filter(project => 
        (project.owner || []).includes(decodedName)
      );
      const maintainedProjectsList = projects.filter(project => 
        (project.maintainers || []).includes(decodedName)
      );
      
      setOwnedProjects(ownedProjectsList);
      setMaintainedProjects(maintainedProjectsList);
      
      // Create person stats - count unique projects and components
      const allProjectsList = [...ownedProjectsList, ...maintainedProjectsList];
      const uniqueProjects = new Set(allProjectsList.map(p => p.id));
      const totalProjects = uniqueProjects.size;
      
      // Count components from unique projects only using actual components array
      const totalComponents = allProjectsList
        .filter((project, index, self) => self.findIndex(p => p.id === project.id) === index) // Remove duplicates
        .reduce((sum, p) => sum + (p.components?.length || 0), 0);
      
      const lastActivity = [...ownedProjectsList, ...maintainedProjectsList]
        .map(p => new Date(p.lastUpdated || '1970-01-01T00:00:00.000Z'))
        .sort((a, b) => b.getTime() - a.getTime())[0]?.toISOString() || '1970-01-01T00:00:00.000Z';
      
      const person: PersonStats = {
        name: decodedName,
        role: ownedProjectsList.length > 0 ? 'Owner' : 'Maintainer',
        projects: allProjectsList
          .filter((project, index, self) => self.findIndex(p => p.id === project.id) === index) // Remove duplicates
          .map(p => p.name),
        totalProjects,
        totalComponents,
        lastActivity
      };
      
      setPersonStats(person);
    }
  }, [projects, personName]);

  // Handle errors from context
  useEffect(() => {
    if (contextError) {
      setError(contextError);
    } else {
      setError(null);
    }
  }, [contextError]);

  // Use loading state from context

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error || !personName) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Error Loading Person</h1>
          <p className="text-slate-600 dark:text-slate-300 mb-4">{error || 'Person not found'}</p>
          <Link to="/people" className="btn-primary">
            Back to People
          </Link>
        </div>
      </div>
    );
  }

  const decodedPersonName = decodeURIComponent(personName || '');
  const totalProjects = personStats?.totalProjects || ownedProjects.length + maintainedProjects.length;
  const totalComponents = personStats?.totalComponents || components.length;

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-8">
        {/* Back Navigation */}
        <div className="mb-6">
          <Link 
            to="/people" 
            className="inline-flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to People
          </Link>
        </div>

        {/* Person Header */}
        <div className="card liquid-glass mb-8 p-6">
          <div className="text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <User className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold gradient-text mb-2">
              {decodedPersonName}
            </h1>
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              PatternLab Developer
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-2xl mx-auto">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-1">
                  {totalProjects}
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  Total Projects
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400 mb-1">
                  {totalComponents}
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  Total Components
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400 mb-1">
                  {ownedProjects.length}
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  Projects Owned
                </div>
              </div>
            </div>


          </div>
        </div>

        {/* Projects Section */}
        {totalProjects > 0 ? (
          <div className="space-y-8">
            {/* Owner Projects */}
            {ownedProjects.length > 0 && (
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <Star className="w-5 h-5 text-blue-600" />
                  <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                    Projects Owned ({ownedProjects.length})
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {ownedProjects.map((project) => (
                    <Link
                      key={project.name}
                      to={`/projects/${project.id}`}
                      className="card card-hover liquid-glass transition-shadow group cursor-pointer p-6"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                          {project.name}
                        </h3>
                        <span className="badge badge-blue">Owner</span>
                      </div>
                      <p className="text-slate-600 dark:text-slate-300 text-sm mb-4 line-clamp-2">
                        {project.description}
                      </p>
                      <div className="flex items-center justify-between text-sm text-slate-500 dark:text-slate-400">
                        <div className="flex items-center space-x-1">
                          <Grid className="w-4 h-4" />
                          <span>{project.components?.length || 0} components</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(project.lastUpdated)}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Maintainer Projects */}
            {maintainedProjects.length > 0 && (
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <Users className="w-5 h-5 text-green-600" />
                  <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                    Projects Maintained ({maintainedProjects.length})
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {maintainedProjects.map((project) => (
                    <Link
                      key={project.name}
                      to={`/projects/${project.id}`}
                      className="card card-hover liquid-glass transition-shadow group cursor-pointer p-6"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors">
                          {project.name}
                        </h3>
                        <span className="badge badge-green">Maintainer</span>
                      </div>
                      <p className="text-slate-600 dark:text-slate-300 text-sm mb-4 line-clamp-2">
                        {project.description}
                      </p>
                      <div className="flex items-center justify-between text-sm text-slate-500 dark:text-slate-400">
                        <div className="flex items-center space-x-1">
                          <Grid className="w-4 h-4" />
                          <span>{project.components?.length || 0} components</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(project.lastUpdated)}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <User className="mx-auto h-12 w-12 text-slate-400 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
              No projects found
            </h3>
            <p className="text-slate-600 dark:text-slate-300">
              This person is not associated with any projects
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonDetail;
